import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { tripsApi } from '../../api/trips.api';
import { summaryName } from '../../utils/tripLegs';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { toast } from '../ui/Toast';
import { Icon, MapPin } from '../ui/Icon';
import DestinationPicker from './legs/DestinationPicker';
import LegNightsList from './legs/LegNightsList';
import { useDestinationSearch } from './newTrip/useDestinationSearch';
import type { Trip } from '../../types';

/**
 * ADR-0030: the trip's destinations and the nights in each, changeable after
 * creation. The nights must add up to the trip's — the dates of each leg are
 * derived from them, so there is nothing else to keep in step.
 */

interface Props {
  trip: Trip;
  canEdit: boolean;
  onUpdated: (trip: Trip) => void;
  className?: string;
}

interface Draft { key: string; id?: string; name: string; latitude: number | null; longitude: number | null; nights: number }

let draftSeq = 0;

function draftsOf(trip: Trip, total: number): Draft[] {
  const legs = trip.destinations?.length
    ? trip.destinations
    : [{ id: null, name: trip.destination, latitude: trip.destinationLat, longitude: trip.destinationLng, nights: total }];
  return legs.map(l => ({ key: `d${++draftSeq}`, id: l.id ?? undefined, name: l.name, latitude: l.latitude, longitude: l.longitude, nights: l.nights }));
}

export default function EditableTripDestinations({ trip, canEdit, onUpdated, className = '' }: Props) {
  const { t } = useTranslation();
  const total = differenceInCalendarDays(parseISO(trip.endDate.slice(0, 10)), parseISO(trip.startDate.slice(0, 10)));
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  function resolve(name: string, coords?: { lat: number; lng: number }) {
    setQuery('');
    setDrafts(ds => {
      const existing = ds.find(d => d.name === name);
      if (existing) return coords ? ds.map(d => d === existing ? { ...d, latitude: coords.lat, longitude: coords.lng } : d) : ds;
      return [...ds, { key: `d${++draftSeq}`, name, latitude: coords?.lat ?? null, longitude: coords?.lng ?? null, nights: 0 }];
    });
  }
  const search = useDestinationSearch(query, open, resolve);

  function addTyped() {
    search.adoptExactMatch();
    const name = query.trim();
    if (name) resolve(name);
  }

  function move(key: string, delta: -1 | 1) {
    setDrafts(ds => {
      const i = ds.findIndex(d => d.key === key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= ds.length) return ds;
      const next = [...ds];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    setBusy(true);
    try {
      const destinations = await tripsApi.replaceDestinations(trip.id, drafts.map(d => ({
        id: d.id, name: d.name, latitude: d.latitude, longitude: d.longitude, nights: d.nights,
      })));
      onUpdated({ ...trip, destinations, destination: summaryName(destinations) });
      toast(t('trip.destinations.saved'), 'success');
      setOpen(false);
    } catch {
      toast(t('trip.destinations.failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const label = summaryName(trip.destinations?.length ? trip.destinations : [{ name: trip.destination }]);
  const valid = drafts.length > 0 && drafts.reduce((a, d) => a + d.nights, 0) === total;

  if (!canEdit) return <p className={className}>{label}</p>;

  return (
    <>
      <button
        type="button"
        onClick={() => { setDrafts(draftsOf(trip, total)); setQuery(''); setOpen(true); }}
        aria-label={t('trip.destinations.edit')}
        className={`${className} inline-flex items-center gap-1 hover:underline cursor-pointer text-left`}
      >
        <span className="line-clamp-1">{label}</span>
        <Icon as={MapPin} size={10} className="opacity-70 shrink-0" />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={t('trip.destinations.title')} size="md">
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('trip.destinations.hint')}</p>
          <LegNightsList
            legs={drafts} total={total}
            onNights={(key, nights) => setDrafts(ds => ds.map(d => d.key === key ? { ...d, nights: Math.max(0, nights) } : d))}
            onMove={move}
            onRemove={key => setDrafts(ds => ds.filter(d => d.key !== key))}
          />
          <DestinationPicker
            legs={drafts} showChips={false} autoFocus={false}
            onRemove={() => {}}
            value={query} onChange={setQuery}
            predictions={search.predictions} onSelect={search.select}
            onAddTyped={addTyped} onSubmit={() => {}}
          />
          <div className="flex gap-2">
            <Button variant="cta" className="flex-1 disabled:opacity-50 disabled:pointer-events-none" disabled={!valid} loading={busy} onClick={save}>
              {t('common.save')}
            </Button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
